import React, { useEffect } from "react";
import ScrollReveal from "scrollreveal";
import { HashLink } from "react-router-hash-link";
import Navbar from "../components/Navbar";
import terrain_pattern_lg from "../assets/images/terrain-pattern-lg.webp";
import portCityImage from "../assets/images/locations/port-city.jpg"
import charlotteImage from "../assets/images/locations/charlotte.jpeg"

const locations = [
  {
    id: "Wilmington",
    name: "Wilmington",
    description:
      "Our Wilmington location serves the surrounding coastal communities. As the #1 FORTIFIED Roof installers in North Carolina, we specialize in storm-resistant roofing solutions designed to withstand hurricane-force winds and salt-air corrosion. Our licensed and insured team are insurance claim experts, guiding you through the process to ensure you get the coverage and repairs you need.",
    address: "4608 Mockingbird Ln, Wilmington, NC 28409",
    phone: "+1 (980) 275-9451",
    image: portCityImage,
    imagePosition: "left",
  },
  {
    id: "Charlotte",
    name: "Charlotte",
    description:
      "Our Charlotte location brings the same commitment to professionalism, honesty, and integrity to the Queen City and greater Piedmont region. Whether you need roof inspections, repairs, or a full replacement with extended 25-year warranties through GAF or CertainTeed, our locally owned team delivers the highest standard of service on every project.",
    address: "311 Airport Rd, Rock Hill, SC 29732",
    phone: "+1 (704) 773-5408",
    image: charlotteImage,
    imagePosition: "right",
  },
];

const Locations: React.FC = () => {
  useEffect(() => {
    if (typeof window !== "undefined") {
      const sr = ScrollReveal({ reset: false });

      // Reveal header section
      sr.reveal("#locations-header h2, #locations-header p", {
        origin: "top",
        distance: "20px",
        duration: 600,
        interval: 150,
      });

      // Reveal location cards
      sr.reveal(".location-card", {
        origin: "bottom",
        distance: "40px",
        duration: 800,
        interval: 200,
        delay: 300,
      });
    }
  }, []);

  return (
    <div>
      <Navbar />
      
      {/* Header Section */}
      <div
        id="locations-header"
        style={{ backgroundImage: `url(${terrain_pattern_lg})` }}
        className="relative isolate overflow-hidden bg-gray-50 bg-center bg-cover pt-32 pb-24 sm:pt-40 sm:pb-32"
      >
        <div className="mx-auto max-w-4xl px-6 lg:px-8 text-center">
          <h2 className="font-chakra_petch_sm text-base/7 font-semibold text-[#D2B48C]">
            FIND US NEAR YOU
          </h2>
          <p className="font-chakra_petch_lg mt-2 text-4xl font-semibold tracking-tight text-gray-950 sm:text-5xl">
            Our Locations
          </p>
          <p className="mt-6 text-lg text-gray-700 max-w-2xl mx-auto">
            With locations across North Carolina and South Carolina, HighTide Roofing is your 
            one-stop shop for all things roofing. Each of our offices is staffed with licensed 
            and insured professionals committed to delivering exceptional service with honesty and integrity.
          </p>
        </div>
      </div>

      {/* Location Sections */}
      {locations.map((location, index) => (
        <div
          key={location.id}
          id={location.id}
          className={`location-card relative isolate overflow-hidden py-24 sm:py-32 ${
            index % 2 === 0 ? "bg-white" : "bg-gray-50"
          }`}
        >
          {/* Background pattern for alternating sections */}
          {index % 2 === 0 && (
            <div className="absolute inset-0 -z-10 overflow-hidden">
              <svg
                aria-hidden="true"
                className="absolute left-[max(50%,25rem)] top-0 h-[64rem] w-[128rem] -translate-x-1/2 stroke-gray-200 [mask-image:radial-gradient(64rem_64rem_at_top,white,transparent)]"
              >
                <defs>
                  <pattern
                    x="50%"
                    y={-1}
                    id={`pattern-${location.id}`}
                    width={200}
                    height={200}
                    patternUnits="userSpaceOnUse"
                  >
                    <path d="M100 200V.5M.5 .5H200" fill="none" />
                  </pattern>
                </defs>
                <rect
                  fill={`url(#pattern-${location.id})`}
                  width="100%"
                  height="100%"
                  strokeWidth={0}
                />
              </svg>
            </div>
          )}

          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div
              className={`grid grid-cols-1 gap-x-16 gap-y-12 lg:grid-cols-2 lg:items-center ${
                location.imagePosition === "right" ? "lg:flex-row-reverse" : ""
              }`}
            >
              {/* Image */}
              <div
                className={`${
                  location.imagePosition === "right" ? "lg:order-2" : "lg:order-1"
                }`}
              >
                <div className="overflow-hidden rounded-2xl shadow-xl ring-1 ring-gray-900/10">
                  <img
                    src={location.image}
                    alt={`${location.name} office`}
                    className="w-full h-80 lg:h-96 object-cover"
                  />
                </div>
              </div>

              {/* Content */}
              <div
                className={`${
                  location.imagePosition === "right" ? "lg:order-1" : "lg:order-2"
                }`}
              >
                <h3 className="font-chakra_petch_lg text-3xl font-semibold tracking-tight text-gray-900 sm:text-4xl">
                  {location.name}
                </h3>
                <p className="mt-6 text-lg text-gray-700 leading-relaxed">
                  {location.description}
                </p>
                <dl className="mt-8 space-y-4">
                  <div>
                    <dt className="text-sm font-semibold text-gray-900">Address</dt>
                    <dd className="mt-1 text-base text-gray-600">{location.address}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-semibold text-gray-900">Phone</dt>
                    <dd className="mt-1 text-base text-gray-600">
                      <a href={`tel:${location.phone}`} className="hover:text-gray-900 transition-colors">{location.phone}</a>
                    </dd>
                  </div>
                </dl>
                <div className="mt-8">
                  <HashLink
                    smooth
                    to="/#FreeQuote"
                    className="inline-flex items-center justify-center rounded-md bg-gray-900 px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-gray-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-900 transition-colors"
                  >
                    Get a Free Quote
                  </HashLink>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default Locations;
